import { Popover, PopoverContent, PopoverArrow, PopoverBody } from '@chakra-ui/react';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faGaugeMin } from '@fortawesome/pro-regular-svg-icons/faGaugeMin';
import { faGaugeLow } from '@fortawesome/pro-regular-svg-icons/faGaugeLow';
import { faGaugeMed } from '@fortawesome/pro-regular-svg-icons/faGaugeMed';
import { faGaugeHigh } from '@fortawesome/pro-regular-svg-icons/faGaugeHigh';
import { faGaugeMax } from '@fortawesome/pro-regular-svg-icons/faGaugeMax';
import { FlagBadge, FlagBadgeStyle } from './FlagBadge';
import { HoverPopoverTrigger } from './HoverPopoverTrigger';
import { _js } from '@ifixit/localize';

type DifficultyTheming = {
   icon: IconDefinition;
} & FlagBadgeStyle;

export enum GuideDifficulty {
   VeryEasy = 1,
   Easy = 2,
   Moderate = 3,
   Difficult = 4,
   VeryDifficult = 5,
}

export const DifficultyToolTipLookup: { [_key in GuideDifficulty]: string } = {
   [GuideDifficulty.VeryEasy]: _js('Difficulty rating: Very easy! No disassembly required.'),
   [GuideDifficulty.Easy]: _js(
      'Difficulty rating: Easy! Minimal disassembly, using only basic tools.'
   ),
   [GuideDifficulty.Moderate]: _js(
      'Difficulty rating: Moderate. Follow instructions carefully to succeed.'
   ),
   [GuideDifficulty.Difficult]: _js(
      'Difficulty rating: Difficult. Proceed with caution and patience.'
   ),
   [GuideDifficulty.VeryDifficult]: _js(
      'Difficulty rating: Very. Bring your best skills and tools.'
   ),
};

const DifficultyThemeLookup: { [_key in GuideDifficulty]: DifficultyTheming } = {
   [GuideDifficulty.VeryDifficult]: {
      icon: faGaugeMax,
      themeColor: 'red',
      background: 'red.200',
      borderColor: 'red.300',
      color: 'red.800',
      iconColor: 'red.700',
   },
   [GuideDifficulty.Difficult]: {
      icon: faGaugeHigh,
      themeColor: 'red',
      background: 'red.100',
      borderColor: 'red.300',
      color: 'red.700',
      iconColor: 'red.500',
   },
   [GuideDifficulty.Moderate]: {
      icon: faGaugeMed,
      themeColor: 'amber',
      background: 'amber.100',
   },
   [GuideDifficulty.Easy]: {
      icon: faGaugeLow,
      themeColor: 'green',
      background: 'green.100',
      borderColor: 'green.400',
      color: 'green.700',
      iconColor: 'green.500',
   },
   [GuideDifficulty.VeryEasy]: {
      icon: faGaugeMin,
      themeColor: 'green',
      background: 'green.200',
      borderColor: 'green.400',
      color: 'green.800',
      iconColor: 'green.700',
   },
};

export function DifficultyBadge({
   difficulty,
   difficultyName,
   className,
}: {
   difficulty: GuideDifficulty;
   difficultyName: string;
   className?: string;
}) {
   return (
      <Popover trigger="hover" strategy="fixed">
         <HoverPopoverTrigger>
            <FlagBadge {...DifficultyThemeLookup[difficulty]} className={className}>
               {difficultyName}
            </FlagBadge>
         </HoverPopoverTrigger>
         <PopoverContent>
            <PopoverArrow backgroundColor="gray.800" />
            <PopoverBody borderRadius="md" backgroundColor="gray.800" color="white" fontSize="14px">
               {DifficultyToolTipLookup[difficulty]}
            </PopoverBody>
         </PopoverContent>
      </Popover>
   );
}
